import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LinkIcon from '@mui/icons-material/Link';
import { StatusChips } from '../../components/status-chips';
import { DeleteAction, EditAction, VisibilityAction } from '../../../../components/row-actions';

interface MenuRowProps {
    dragHandle: React.ReactNode;
    title?: string;
    slug?: string;
    url?: string;
    customPageId?: number;
    isDefault: boolean;
    isVisible: boolean;
    /** Registros fixos do portal: sem editar/excluir, apenas reordenar. */
    canEdit?: boolean;
    extra?: React.ReactNode;
    selected?: boolean;
    onClick?: () => void;
    onToggleVisibility: () => void;
    onEdit: () => void;
    onDelete: () => void;
}

export function describeTarget(customPageId?: number, slug?: string, url?: string) {
    if (customPageId) return { icon: <ArticleIcon sx={{ fontSize: 14 }} />, text: `Página /${slug || ''}` };
    if (url?.startsWith('/')) return { icon: <LinkIcon sx={{ fontSize: 14 }} />, text: `Portal ${url}` };
    if (url) return { icon: <OpenInNewIcon sx={{ fontSize: 14 }} />, text: url };
    return { icon: null, text: 'Sem link' };
}

export function MenuRow({ dragHandle, title, slug, url, customPageId, isDefault, isVisible, canEdit = true, extra, selected, onClick, onToggleVisibility, onEdit, onDelete }: MenuRowProps) {
    const target = describeTarget(customPageId, slug, url);

    return (
        <Paper
            variant="outlined"
            onClick={onClick}
            sx={{
                display: 'flex', alignItems: 'center', gap: 1, p: 1, pr: 0.5,
                cursor: onClick ? 'pointer' : 'default',
                borderColor: selected ? 'primary.main' : 'divider',
                bgcolor: selected ? 'action.selected' : 'background.paper',
                borderLeftWidth: selected ? 3 : 1,
                transition: 'all 0.15s ease-in-out',
                '&:hover': onClick ? { bgcolor: selected ? 'action.selected' : 'action.hover' } : undefined,
            }}
        >
            {dragHandle}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap sx={{ opacity: isVisible ? 1 : 0.6 }}>{title}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                    {target.icon}
                    <Typography variant="caption" noWrap>{target.text}</Typography>
                    {extra}
                </Box>
            </Box>
            <StatusChips isDefault={isDefault} isVisible={isVisible} />
            {canEdit && (
                // As ações não selecionam o menu da linha
                <Box sx={{ display: 'flex' }} onClick={(e) => e.stopPropagation()}>
                    <VisibilityAction isVisible={isVisible} onClick={onToggleVisibility} />
                    <EditAction onClick={onEdit} />
                    <DeleteAction title={isDefault ? 'Registros padrão não podem ser excluídos' : 'Excluir'} disabled={isDefault} onClick={onDelete} />
                </Box>
            )}
        </Paper>
    );
}
